import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, RotateCcw, Trash2, Info, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Pager = ({ offset, limit, total, onChange, testIdPrefix }) => {
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const currentPage = Math.floor(offset / limit) + 1;
  if (total <= limit) return null;
  return (
    <div className="flex items-center justify-between border-t pt-2 text-xs text-gray-600">
      <span data-testid={`${testIdPrefix}-page-info`}>
        Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(offset - limit, 0))}
          data-testid={`${testIdPrefix}-prev-btn`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        <span className="px-2">Page {currentPage} / {totalPages}</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={offset + limit >= total}
          onClick={() => onChange(offset + limit)}
          data-testid={`${testIdPrefix}-next-btn`}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

/**
 * StagingTrashModal — lists soft-deleted (Delete) staging rows.
 * Search (client-triggered, server-side substring), pagination (server-side),
 * and Restore per row.
 */
export const StagingTrashModal = ({ open, onOpenChange, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [restoring, setRestoring] = useState(new Set());

  // Debounce the search input so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset pagination on new search
  useEffect(() => { setOffset(0); }, [debouncedQ]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await axios.get(`${API}/admin/bulk-import/staging/trash?${params}`, { headers: authHeaders() });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load trash');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offset, debouncedQ]);

  const restore = async (ids) => {
    setRestoring(new Set([...restoring, ...ids]));
    try {
      const r = await axios.post(
        `${API}/admin/bulk-import/staging/trash/bulk-restore`,
        { trash_ids: ids },
        { headers: authHeaders() },
      );
      toast.success(r.data.message || `Restored ${r.data.restored} row(s)`);
      // If the last row on the page was restored, walk back one page.
      const remaining = total - r.data.restored;
      const maxOffsetForRemaining = Math.max(remaining - 1, 0);
      if (offset > maxOffsetForRemaining) setOffset(Math.max(offset - PAGE_SIZE, 0));
      else await load();
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      const next = new Set(restoring);
      ids.forEach((i) => next.delete(i));
      setRestoring(next);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="staging-trash-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-500" />
            Staging Trash
            <Badge variant="outline" className="ml-2">{total}</Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Soft-deleted bulk-import staging rows. Rows land here for 7 days and can be restored back to staging with their original filename and batch intact.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-gray-500 flex items-start gap-1 -mt-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            Rows soft-deleted via <strong>Delete</strong> land here for 7 days, then auto-purge.
            On restore, the row goes back to staging with its <em>original filename &amp; batch</em>
            intact — even if the rest of that batch is already approved.
          </span>
        </div>

        {/* Search + Restore-all row */}
        <div className="flex items-center gap-2 border-y py-2">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search question text, filename, or who deleted it…"
              className="pl-7 h-8 text-xs"
              data-testid="trash-search-input"
            />
          </div>
          {items.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => restore(items.map((i) => i.id))}
              disabled={loading || restoring.size > 0}
              data-testid="trash-restore-all-btn"
              title="Restore every row currently visible on this page"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Restore page
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              {debouncedQ ? 'No trashed rows match your search.' : 'Trash is empty. Skipped rows will appear here.'}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it.id} className="py-2 flex items-start gap-3" data-testid={`trash-row-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800" title={it.question_text}>
                      {it.question_text || '(no text)'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span>Grade: {it.grade || '—'}</span>
                      <span>File: <span className="text-gray-700">{it.source_filename || '—'}</span></span>
                      <span>Batch: <code>{(it.import_batch_id || '').slice(0, 8)}</code></span>
                      <span>Deleted by: {it.deleted_by_email || '—'}</span>
                      <span>At: {(it.deleted_at_iso || '').slice(0, 19).replace('T', ' ')}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => restore([it.id])}
                    disabled={restoring.has(it.id)}
                    data-testid={`trash-restore-${it.id}-btn`}
                  >
                    {restoring.has(it.id)
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><RotateCcw className="w-3 h-3 mr-1" /> Restore</>}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Pager
          offset={offset}
          limit={PAGE_SIZE}
          total={total}
          onChange={setOffset}
          testIdPrefix="trash"
        />
      </DialogContent>
    </Dialog>
  );
};

